/**
 * `outcomes.ts` — the multi-outcome board's pure rollup + lane geometry
 * (#1043, B2.14 / Track-B PR-B). Pure + dependency-free (apart from the tree
 * types), so it's fully unit-testable and re-usable.
 *
 * A "Map" can hold several outcome roots (the model has always permitted a
 * forest; `buildTree` returns one `TreeNode` per root). When there are 2+ the
 * editorial canvas frames each root's subtree under a "lane" header band — an
 * outcome title + a "% of work done" progress tag — so the board reads as
 * several parallel outcomes, not one undifferentiated tree (the wireframe
 * `V_B214`).
 *
 * Two clean, separate concerns live here (the clean-abstraction discipline,
 * `feedback_taskmap_clean_code_standards`):
 *
 *   1. `outcomeRollup` — the PROGRESS semantic, about the WORK, never a person
 *      (brand firewall §A6/§A9). It counts LEAVES (the smallest units of work):
 *      `totalLeaves` = every leaf descendant; `doneLeaves` = the leaves in the
 *      `done` state. This is the agreed "% of work done" measure and is shared
 *      with the Home dashboard (PR-D), so it is deliberately general — it takes
 *      a `TreeNode` root and knows nothing about layout or the editorial look.
 *
 *   2. `outcomeLanes` — the lane GEOMETRY. Given the roots and the
 *      already-computed `TreeLayout` (so layout is never re-run), it returns
 *      one `OutcomeLane` per root: the horizontal extent the root's positioned
 *      subtree spans on the canvas (min x → max x+width), plus the root's title
 *      and its rollup. The canvas draws the band from that.
 */

import type { TreeNode } from "./buildTree";
import { flattenTree } from "./buildTree";
import type { TreeLayout } from "./layout";
import type { TaskState } from "./state";

/**
 * Leaf-based progress for one outcome (root) subtree — the "% of work done"
 * semantic. A leaf is a node with zero children (the smallest unit of work). A
 * lone root with no children counts as one leaf (itself), so a brand-new
 * single-card outcome reads as "0 of 1 done" rather than "0 of 0".
 *
 * Counts WORK, never people: `done` is the leaf's task state, not anyone's
 * completion count (brand firewall §A6/§A9).
 */
export function outcomeRollup(root: TreeNode): { doneLeaves: number; totalLeaves: number } {
  let doneLeaves = 0;
  let totalLeaves = 0;
  const walk = (node: TreeNode): void => {
    if (node.children.length === 0) {
      // A leaf — the smallest unit of work (incl. a lone root, which is its
      // own single leaf).
      totalLeaves += 1;
      if (node.state === "done") doneLeaves += 1;
      return;
    }
    for (const child of node.children) walk(child);
  };
  walk(root);
  return { doneLeaves, totalLeaves };
}

/**
 * Home-dashboard rollup (#1043, PR-D) — one outcome card's factual numbers. It
 * EXTENDS `outcomeRollup` (reused verbatim for the leaf progress) with two more
 * counts the Home strip surfaces:
 *
 *   - `blockedCount` — nodes anywhere in the subtree whose state is `blocked`.
 *     Counts WORK ITEMS that are blocked, never a person; rendered in calm
 *     amber, never red (brand firewall §A9).
 *   - `dueSoonCount` — nodes anywhere in the subtree that have a `targetDate`
 *     on or before the soon-horizon (today + `DUE_SOON_DAYS`, INCLUSIVE of
 *     anything already overdue) AND are not yet `done`. A `done` node is never
 *     "due soon" — the work is finished. A node with no `targetDate` never
 *     counts.
 *
 * Both counts walk EVERY node in the subtree (not just leaves) — a blocked or
 * due-soon parent is itself a real, surfaced work item on the issues list, so
 * it should be counted. The leaf-based `doneLeaves`/`totalLeaves` stays the
 * progress measure (the "% of work done" bar); these two are independent
 * factual annotations beside it.
 *
 * `now` is injected (never `Date.now()` inline) so the surface and the tests
 * pin the same clock — the due-soon boundary is otherwise untestable.
 */
export interface OutcomeStats {
  /** Leaf-based done count (from `outcomeRollup`). */
  doneLeaves: number;
  /** Leaf-based total count (from `outcomeRollup`). */
  totalLeaves: number;
  /** Subtree nodes in the `blocked` state (amber, never a person). */
  blockedCount: number;
  /** Subtree nodes due on/before today+7 (incl. overdue) and not yet done. */
  dueSoonCount: number;
}

/** Days ahead (inclusive of overdue) that count a dated, unfinished node as "due soon". */
export const DUE_SOON_DAYS = 7;

/**
 * The local-midnight UTC-day boundary, `DUE_SOON_DAYS` days from `now`, as a
 * `YYYY-MM-DD` string. Comparing the node's `target_date` (already a date-only
 * `YYYY-MM-DD`) against this as plain strings is timezone-agnostic and avoids a
 * `Date` parse of the node date entirely — lexical date-string order matches
 * chronological order for the ISO `YYYY-MM-DD` format.
 */
function soonHorizonDate(now: Date): string {
  const horizon = new Date(now);
  horizon.setDate(horizon.getDate() + DUE_SOON_DAYS);
  return toIsoDate(horizon);
}

/** A `Date` → its `YYYY-MM-DD` calendar day (in the runtime's local zone). */
function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * True when a node is "due soon": it has a `targetDate`, is not `done`, and the
 * date is on or before the soon-horizon (so overdue dates count too). Pure +
 * string-comparison only (see `soonHorizonDate`).
 */
export function isDueSoon(
  node: { state: TaskState; targetDate: string | null },
  now: Date,
): boolean {
  if (node.state === "done") return false;
  if (!node.targetDate) return false;
  return node.targetDate <= soonHorizonDate(now);
}

/** True when a dated, unfinished node's `targetDate` is strictly before today. */
export function isOverdue(
  node: { state: TaskState; targetDate: string | null },
  now: Date,
): boolean {
  if (node.state === "done") return false;
  if (!node.targetDate) return false;
  return node.targetDate < toIsoDate(now);
}

/**
 * The Home strip's per-outcome numbers. Reuses `outcomeRollup` for the leaf
 * progress, then adds the blocked + due-soon subtree counts in a single walk.
 */
export function outcomeStats(root: TreeNode, now: Date): OutcomeStats {
  const { doneLeaves, totalLeaves } = outcomeRollup(root);
  let blockedCount = 0;
  let dueSoonCount = 0;
  for (const node of flattenTree([root])) {
    if (node.state === "blocked") blockedCount += 1;
    if (isDueSoon(node, now)) dueSoonCount += 1;
  }
  return { doneLeaves, totalLeaves, blockedCount, dueSoonCount };
}

/**
 * The completed-plan ("archived") treatment is DERIVED, not stored (#1043,
 * B8.4 / Track-B PR). An outcome is COMPLETE when it has at least one leaf of
 * work AND every leaf is `done` — i.e. `totalLeaves > 0 && doneLeaves ===
 * totalLeaves`. Pure + counts WORK, never people (brand firewall §A6/§A9): it
 * reads the leaf progress and nothing about who did it.
 *
 * Takes the leaf-count pair (the shape `outcomeRollup` returns, and the subset
 * of `OutcomeStats` the Home strip already holds), so the canvas, the editor,
 * the member view and the Home dashboard all derive completion from one place
 * without re-walking. A brand-new lone root reads `0 of 1` → NOT complete (the
 * work hasn't been done); reopening any leaf off `done` un-completes it again.
 *
 * "Complete" is a GOOD state — surfaced in calm done-green, never red/amber.
 */
export function isOutcomeComplete(rollup: { doneLeaves: number; totalLeaves: number }): boolean {
  return rollup.totalLeaves > 0 && rollup.doneLeaves === rollup.totalLeaves;
}

/**
 * The WHOLE board is complete (#1043, B8.4) when there is at least one task
 * (some outcome with a leaf) AND every outcome is complete. Drives the top
 * "PLAN COMPLETED" banner + the member read-only note. An EMPTY map (no roots,
 * or only lone never-started roots) is never "complete" — there's no finished
 * work to celebrate. Pure: derives from `outcomeRollup` per root, no clock.
 */
export function isBoardComplete(roots: readonly TreeNode[]): boolean {
  if (roots.length === 0) return false;
  return roots.every((root) => isOutcomeComplete(outcomeRollup(root)));
}

/**
 * The total number of leaves of work across the forest — the "All N leaves
 * done." count the completed banner names. Pure; sums `outcomeRollup` totals.
 */
export function totalLeafCount(roots: readonly TreeNode[]): number {
  return roots.reduce((sum, root) => sum + outcomeRollup(root).totalLeaves, 0);
}

/**
 * One outcome lane's header-band geometry + content. `x`/`width` are in the
 * SAME (unpadded) layout coordinate space the positioned nodes use, so the
 * canvas adds its `CANVAS_PAD` once to place the band exactly over the root's
 * subtree.
 */
export interface OutcomeLane {
  /** The outcome root's node id. */
  rootId: string;
  /** The outcome's title (the root's title — serif-italic on the band). */
  title: string;
  /** Left edge of the band, in layout coords (min x across the subtree). */
  x: number;
  /** Band width, in layout coords (max x+width − min x across the subtree). */
  width: number;
  /** Done-leaf count (the rollup's `doneLeaves`). */
  doneLeaves: number;
  /** Total-leaf count (the rollup's `totalLeaves`). */
  totalLeaves: number;
}

/**
 * Compute one lane per root from the already-computed layout. Pure: it never
 * re-runs layout — it intersects each root's subtree node-ids
 * (`flattenTree([root])`) with the positioned `layout.nodes` and takes the
 * min `x` / max `x+width` of that intersection as the band's horizontal extent.
 *
 * A root whose descendants are all collapsed (so only the root box is laid out)
 * still yields its own extent — the root card's own box. A root with no laid-out
 * node at all (shouldn't happen, but defensive) is skipped so we never emit a
 * zero-width or NaN band.
 */
export function outcomeLanes(roots: readonly TreeNode[], layout: TreeLayout): OutcomeLane[] {
  const posById = new Map(layout.nodes.map((p) => [p.node.id, p]));
  const lanes: OutcomeLane[] = [];

  for (const root of roots) {
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    for (const node of flattenTree([root])) {
      const pos = posById.get(node.id);
      if (!pos) continue; // collapsed/absent descendant — not on the canvas
      minX = Math.min(minX, pos.x);
      maxX = Math.max(maxX, pos.x + pos.width);
    }
    // No laid-out node for this root → no band (defensive; never NaN/negative).
    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) continue;

    const { doneLeaves, totalLeaves } = outcomeRollup(root);
    lanes.push({
      rootId: root.id,
      title: root.title,
      x: minX,
      width: maxX - minX,
      doneLeaves,
      totalLeaves,
    });
  }

  return lanes;
}

// ── Home dashboard: the filterable issues list (#1043, PR-D) ───────────────
//
// The Home overview lists every NON-ROOT task across the forest as a flat,
// GitHub-issues-style row set. The pure builder below flattens the forest into
// `TaskIssueRow`s carrying everything a row needs to render and to be filtered
// — kept pure (no clock-default; `now` is injected) so the sort + the
// overdue/due-soon flags are fully unit-testable.

/** One row on the Home issues list — a non-root task plus its display facts. */
export interface TaskIssueRow {
  /** The task node (the row's source of truth — title/state/assignees/date). */
  node: TreeNode;
  /** The outcome (root) this task belongs to — its id + title (the row's "lane"). */
  outcomeId: string;
  outcomeTitle: string;
  /** Due on/before today+7 (incl. overdue) and not done — amber date treatment. */
  dueSoon: boolean;
  /** Strictly before today and not done — also amber (a stronger sort weight). */
  overdue: boolean;
}

/** Sort weight for the default ordering's secondary state tiebreak (lower = first). */
const STATE_SORT_WEIGHT: Record<TaskState, number> = {
  blocked: 0,
  in_progress: 1,
  planned: 2,
  done: 3,
};

/**
 * Flatten the forest to the Home issues list: every NON-ROOT task (the outcome
 * roots are the strip's cards, not list rows), each annotated with its owning
 * outcome + due flags. Default order = **due soonest first, undated last, then
 * by state** (blocked → in progress → planned → done), title as the final
 * tiebreak so the order is deterministic across renders + tests.
 *
 * Pure: `now` is injected (no inline clock) so the due-flag boundary + the sort
 * are pinned in tests. Counts/sorts WORK, never people (brand firewall §A9).
 */
export function buildIssueRows(roots: readonly TreeNode[], now: Date): TaskIssueRow[] {
  const rows: TaskIssueRow[] = [];
  for (const root of roots) {
    // Skip the root itself — it's the outcome (a strip card), not a list row.
    // Its descendants are the tasks.
    for (const node of flattenTree([root])) {
      if (node.id === root.id) continue;
      rows.push({
        node,
        outcomeId: root.id,
        outcomeTitle: root.title,
        dueSoon: isDueSoon(node, now),
        overdue: isOverdue(node, now),
      });
    }
  }

  rows.sort((a, b) => {
    // 1) Dated rows before undated rows; among dated, soonest date first.
    const da = a.node.targetDate;
    const db = b.node.targetDate;
    if (da && db) {
      if (da !== db) return da < db ? -1 : 1;
    } else if (da && !db) {
      return -1;
    } else if (!da && db) {
      return 1;
    }
    // 2) State weight (blocked first, done last).
    const sa = STATE_SORT_WEIGHT[a.node.state];
    const sb = STATE_SORT_WEIGHT[b.node.state];
    if (sa !== sb) return sa - sb;
    // 3) Title, then id — deterministic final tiebreak.
    const t = a.node.title.localeCompare(b.node.title);
    if (t !== 0) return t;
    return a.node.id.localeCompare(b.node.id);
  });

  return rows;
}

/**
 * The "Your work" slice (round-4 item ④, #1043) — the rows from `buildIssueRows`
 * that the signed-in `membershipId` is an assignee on AND that need attention
 * NOW: overdue · due-soon · blocked. Reuses the same `TaskIssueRow` shape so
 * the dashboard can render it through the same row component as the main list.
 *
 * Pure: takes the already-built forest + `now`; no fetch, no clock. Returns an
 * empty list when `membershipId` is `null` (anonymous / not on this team) so a
 * defensive caller can mount the same way for everyone. Counts WORK, never
 * people (brand firewall §A9): this is a personal SLICE, never a per-person
 * tally / ranking — the rows are tasks, the criterion is "you're on it".
 */
export function buildYourWorkRows(
  roots: readonly TreeNode[],
  membershipId: string | null,
  now: Date,
): TaskIssueRow[] {
  if (!membershipId) return [];
  return buildIssueRows(roots, now).filter(
    (r) =>
      r.node.assigneeIds.includes(membershipId) &&
      (r.overdue || r.dueSoon || r.node.state === "blocked"),
  );
}
